import React, { useState, useEffect } from 'react';
import { Briefcase, Search, MapPin, DollarSign, Clock, Building2, Send, Loader2, FileText, CheckCircle, Upload, User, Download, Calendar, Users, Award, ArrowLeft, ArrowRight, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudentJobPortal = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [jobTypeFilter, setJobTypeFilter] = useState('');
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applicationData, setApplicationData] = useState({
    resume_url: '',
    cover_letter: ''
  });
  const [activeTab, setActiveTab] = useState('jobs');
  const [resumeData, setResumeData] = useState({ resume_url: '', resume_filename: '', resume_updated_at: '' });
  const [uploadingResume, setUploadingResume] = useState(false);
  
  const token = localStorage.getItem('studentToken') || localStorage.getItem('token');
  const studentInfo = localStorage.getItem('studentInfo') ? JSON.parse(localStorage.getItem('studentInfo')) : null;
  const studentId = studentInfo?.student_id || localStorage.getItem('studentId');
  const studentName = studentInfo?.student_name || 'Student';

  useEffect(() => {
    fetchJobs();
    fetchMyApplications();
    if (studentId) {
      fetchResume();
    }
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/placement/jobs?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyApplications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/placement/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  const fetchResume = async () => {
    if (!studentId) return;
    try {
      const response = await axios.get(`${API_URL}/api/placement/student/${studentId}/resume`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setResumeData(response.data);
      }
    } catch (error) {
      console.error('Error fetching resume:', error);
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF or Word document');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should be less than 5MB');
      return;
    }

    setUploadingResume(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', 'ml_default');
      formData.append('resource_type', 'raw');

      const cloudinaryResponse = await axios.post(
        `https://api.cloudinary.com/v1_1/dqqjuuauz/raw/upload`,
        formData
      );

      const resume_url = cloudinaryResponse.data.secure_url;

      await axios.post(
        `${API_URL}/api/placement/student/resume?student_id=${studentId}`,
        { resume_url, resume_filename: file.name },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResumeData({
        resume_url,
        resume_filename: file.name,
        resume_updated_at: new Date().toISOString()
      });

      toast.success('Resume uploaded successfully!');
    } catch (error) {
      console.error('Error uploading resume:', error);
      toast.error('Failed to upload resume');
    } finally {
      setUploadingResume(false);
    }
  };

  const openApplyModal = (job) => {
    setSelectedJob(job);
    setApplicationData({
      resume_url: resumeData.resume_url || '',
      cover_letter: ''
    });
    setShowApplyModal(true);
  };

  const handleApply = async () => {
    if (!resumeData.resume_url) {
      toast.error('Please upload your resume first from My Profile tab');
      return;
    }

    setApplying(true);
    try {
      await axios.post(
        `${API_URL}/api/placement/jobs/${selectedJob.id}/apply`,
        {
          resume_url: resumeData.resume_url,
          cover_letter: applicationData.cover_letter
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Application submitted successfully!');
      setShowApplyModal(false);
      fetchMyApplications();
      fetchJobs();
    } catch (error) {
      console.error('Error applying:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  const isApplied = (jobId) => myApplications.some(app => app.job_id === jobId);

  const getStatusStyle = (status) => {
    const styles = {
      applied: 'bg-blue-100 text-blue-700',
      shortlisted: 'bg-yellow-100 text-yellow-700',
      interview_scheduled: 'bg-purple-100 text-purple-700',
      selected: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      offer_accepted: 'bg-emerald-100 text-emerald-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const formatSalary = (min, max) => {
    if (!min && !max) return 'Not Disclosed';
    const format = (n) => n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : `₹${n?.toLocaleString('en-IN')}`;
    if (min && max) return `${format(min)} - ${format(max)}`;
    return min ? `${format(min)}+` : `Up to ${format(max)}`;
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !jobTypeFilter || job.job_type === jobTypeFilter;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/student/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Job Opportunities</h1>
                <p className="text-sm text-gray-500">Find and apply for campus placements</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-900">{jobs.length}</p>
                <p className="text-gray-500">Active Jobs</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-blue-600">{myApplications.length}</p>
                <p className="text-gray-500">Applied</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-green-600">{myApplications.filter(a => a.status === 'selected').length}</p>
                <p className="text-gray-500">Selected</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b">
          <button
            onClick={() => setActiveTab('jobs')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'jobs'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Available Jobs ({jobs.length})
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'applications'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            My Applications ({myApplications.length})
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'profile'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            My Profile
            {!resumeData.resume_url && (
              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
            )}
          </button>
        </div>

        {activeTab === 'jobs' && (
          <>
            {/* Search & Filter */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Search jobs by title or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
              </div>
              <select
                value={jobTypeFilter}
                onChange={(e) => setJobTypeFilter(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white min-w-[150px]"
              >
                <option value="">All Types</option>
                <option value="Full Time">Full Time</option>
                <option value="Part Time">Part Time</option>
                <option value="Internship">Internship</option>
                <option value="Contract">Contract</option>
              </select>
            </div>

            {/* Jobs List */}
            <div className="space-y-4">
              {filteredJobs.map(job => (
                <div key={job.id} className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 font-semibold text-lg">
                        {job.company_name?.charAt(0) || 'C'}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
                        <p className="text-blue-600 font-medium">{job.company_name}</p>
                        <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
                          {job.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" /> {job.location}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-4 w-4" /> {formatSalary(job.salary_range_min, job.salary_range_max)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-4 w-4" /> {job.vacancies} positions
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" /> {job.job_type}
                          </span>
                        </div>
                        {job.skills_required?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {job.skills_required.slice(0, 4).map(skill => (
                              <span key={skill} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                {skill}
                              </span>
                            ))}
                            {job.skills_required.length > 4 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs">
                                +{job.skills_required.length - 4}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                        job.job_type === 'Full Time' ? 'bg-green-100 text-green-700' :
                        job.job_type === 'Internship' ? 'bg-purple-100 text-purple-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {job.job_type}
                      </span>
                      {job.application_deadline && (
                        <span className="text-xs text-gray-500">Deadline: {job.application_deadline}</span>
                      )}
                      {isApplied(job.id) ? (
                        <button disabled className="mt-2 px-4 py-2 bg-gray-100 text-gray-500 rounded-lg text-sm flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" /> Applied
                        </button>
                      ) : (
                        <button
                          onClick={() => openApplyModal(job)}
                          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                        >
                          Apply Now
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {filteredJobs.length === 0 && (
                <div className="text-center py-12 bg-white rounded-lg border">
                  <Briefcase className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <h3 className="text-lg font-medium text-gray-700">No Jobs Available</h3>
                  <p className="text-gray-500 text-sm">Check back later for new opportunities</p>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'applications' && (
          <div className="space-y-4">
            {myApplications.length > 0 ? (
              myApplications.map(app => (
                <div key={app.id} className="bg-white rounded-lg border border-gray-200 p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 font-semibold">
                        {app.company_name?.charAt(0) || 'C'}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{app.job_title}</h3>
                        <p className="text-blue-600 font-medium">{app.company_name}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          Applied: {formatDate(app.applied_at)}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusStyle(app.status)}`}>
                      {app.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </div>

                  {app.status === 'interview_scheduled' && app.interview_date && (
                    <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-sm font-medium text-purple-700 flex items-center gap-2">
                        <Calendar className="h-4 w-4" /> Interview Scheduled
                      </p>
                      <p className="text-sm text-purple-600 mt-1">
                        {app.interview_date} at {app.interview_time || 'TBA'}
                        {app.interview_venue && ` • ${app.interview_venue}`}
                      </p>
                    </div>
                  )}

                  {app.status === 'selected' && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm font-medium text-green-700 flex items-center gap-2">
                        <Award className="h-4 w-4" /> Congratulations! You have been selected!
                      </p>
                      {app.offer_salary && (
                        <p className="text-sm text-green-600 mt-1">
                          Offered Package: ₹{(app.offer_salary / 100000).toFixed(1)} LPA
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-12 bg-white rounded-lg border">
                <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <h3 className="text-lg font-medium text-gray-700">No Applications Yet</h3>
                <p className="text-gray-500 text-sm mb-4">Start applying to track your applications</p>
                <button
                  onClick={() => setActiveTab('jobs')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                >
                  Browse Jobs
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="max-w-xl space-y-6">
            {/* Resume Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">My Resume</h3>
              <p className="text-sm text-gray-500 mb-4">
                Your resume will be automatically attached when you apply for jobs.
              </p>
              
              {resumeData.resume_url ? (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="font-medium text-gray-900">{resumeData.resume_filename || 'Resume.pdf'}</p>
                        <p className="text-sm text-gray-500">
                          Updated: {resumeData.resume_updated_at ? formatDate(resumeData.resume_updated_at) : 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <a
                        href={resumeData.resume_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                      >
                        View
                      </a>
                      <label className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer text-sm">
                        {uploadingResume ? 'Uploading...' : 'Update'}
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleResumeUpload}
                          className="hidden"
                          disabled={uploadingResume}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <Upload className="h-10 w-10 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600 mb-2">No resume uploaded</p>
                  <p className="text-sm text-gray-500 mb-4">PDF or Word document, max 5MB</p>
                  <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer text-sm">
                    {uploadingResume ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="h-4 w-4" /> Upload Resume</>
                    )}
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleResumeUpload}
                      className="hidden"
                      disabled={uploadingResume}
                    />
                  </label>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Stats</h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-semibold text-gray-900">{myApplications.length}</p>
                  <p className="text-xs text-gray-500">Applied</p>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <p className="text-2xl font-semibold text-yellow-600">
                    {myApplications.filter(a => ['shortlisted', 'interview_scheduled'].includes(a.status)).length}
                  </p>
                  <p className="text-xs text-gray-500">In Progress</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-semibold text-green-600">
                    {myApplications.filter(a => a.status === 'selected').length}
                  </p>
                  <p className="text-xs text-gray-500">Selected</p>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-semibold text-purple-600">
                    {myApplications.filter(a => a.status === 'interview_scheduled').length}
                  </p>
                  <p className="text-xs text-gray-500">Interviews</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Apply Modal */}
      {showApplyModal && selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl">
            <div className="p-5 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Apply for Job</h2>
                  <p className="text-sm text-gray-500">{selectedJob.title} at {selectedJob.company_name}</p>
                </div>
                <button onClick={() => setShowApplyModal(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Resume</label>
                {resumeData.resume_url ? (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200 flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="flex-1 text-sm text-green-700">{resumeData.resume_filename}</span>
                    <a href={resumeData.resume_url} target="_blank" rel="noopener noreferrer" className="text-sm text-green-600 hover:underline">
                      View
                    </a>
                  </div>
                ) : (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm text-red-700">Please upload resume from My Profile first</p>
                    <button 
                      onClick={() => { setShowApplyModal(false); setActiveTab('profile'); }}
                      className="text-sm text-red-600 hover:underline mt-1"
                    >
                      Upload Now →
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cover Letter (Optional)</label>
                <textarea
                  rows="4"
                  value={applicationData.cover_letter}
                  onChange={(e) => setApplicationData({ ...applicationData, cover_letter: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Why are you a good fit for this role?"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowApplyModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  disabled={applying || !resumeData.resume_url}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {applying ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentJobPortal;
